import ButtonsBlock from '@/components/common/buttons-block/buttons-block';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import FormInputsSeparator from '@/components/common/form-inputs-separator/form-inputs-separator';
import useGetAvatar from '@/hooks/useGetAvatar';
import useGetCurrentUserV2 from '@/hooks/useGetCurrentUser';
import useGetGroupAvatars from '@/hooks/useGetGroupAvatars';
import useGetMetadataAvatarTypes from '@/hooks/useGetMetadataAvatarTypes';
import { composeUri } from '@/utils/compose';
import {
    AUDIT_METADATA_AVATAR_TYPE_NAME,
    AUDIT_STATE_TAKE_INTO_ACCOUNT_HUMAN_ID,
    AVATAR_GROUP_AUDIT_TYPE,
    AVATAR_GROUP_QUOTE_TYPE,
    AVATARS,
    AVATARS_ADD_GROUP_AVATARS,
    CHANGE_DEMANDS_TO,
    FINGER_ID,
    FINGERS,
    GROUP_AVATAR_SITE_PREFIX,
    GROUP_AVATARS,
    INCIDENT_SERVICE_CLIENT,
    INTERVENTION_CREATED_FROM_AVATAR_PROPERTY,
    INTERVENTION_METADATA_AVATAR_TYPE_NAME,
    INTERVENTION_TYPE_NAME_AUDIT_OUT_OF_CONTRACT,
    INTERVENTION_TYPE_NAME_UNDER_QUOTE,
    INTERVENTION_TYPE_NAME_VALUES,
    PLACES,
    TRACEABILITY_TRACKING_V2_CHANGE_NODE_1,
} from '@/utils/CONST';
import { Avatar } from '@/utils/types';
import useGetAlphaId from '@/utils/useGetAlphaId';
import { joiResolver } from '@hookform/resolvers/joi';
import { FiberNew } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useQueryClient } from '@tanstack/react-query';
import { composeUriId } from 'base-tools-360smartconnect/utils/compose';
import Joi from 'joi';
import { get, map, merge, set } from 'lodash';
import moment from 'moment/moment';
import { useMemo } from 'react';
import {
    AutocompleteInput,
    Form,
    FormDataConsumer,
    ReferenceInput,
    SelectInput,
    useCreate,
    useNotify,
    useTranslate,
} from 'react-admin';
import { useParams } from 'react-router';

const addZeros = (value: number) => String(value).padStart(4, '0');

interface CreateInterventionProps {
    open: boolean;
    onClose: () => void;
}

const CreateIntervention = ({ open, onClose }: CreateInterventionProps) => {
    const translate = useTranslate();
    const handleClose = () => {
        onClose();
    };
    const notify = useNotify();
    const [create, { isLoading: isLoadingCreate }] = useCreate();
    const { placeId, siteId } = useParams();
    const { firstMetadataAvatarType: metadataAvatarType, isLoading } = useGetMetadataAvatarTypes({
        filters: {
            name: INTERVENTION_METADATA_AVATAR_TYPE_NAME,
        },
    });
    const { isLoadingGetAlphaId, getAlphaId } = useGetAlphaId({
        service: INCIDENT_SERVICE_CLIENT,
    });
    // const refresh = useRefresh();
    const queryClient = useQueryClient();

    const { avatar, isLoading: isLoadingPlace } = useGetAvatar({
        avatarUriId: composeUri(AVATARS, placeId),
        queryKey: `${PLACES}_${siteId}`,
    });
    const placeAlphaId: any = useMemo(() => get(avatar, 'alphaId'), [avatar]);
    const { currentUser } = useGetCurrentUserV2();

    const {
        groupAvatars,
        firstGroupAvatarId,
        isLoading: isLoadingGroup,
    } = useGetGroupAvatars({
        filters: {
            alphaId: `${GROUP_AVATAR_SITE_PREFIX}${siteId}`,
        },
    });

    const handleSubmit = (values: any) => {
        getAlphaId().then((data: { counter: number; identifier: string }) => {
            create(
                AVATARS,
                {
                    data: {
                        parent: composeUri(AVATARS, placeId),
                        name: 'Intervention',
                        alphaId: `I:${data.counter}`,
                        mcs: [],
                        generateMCFinger: composeUriId(FINGERS, FINGER_ID),
                        generateMCQuantity: 1,
                        properties: [
                            {
                                name: INTERVENTION_CREATED_FROM_AVATAR_PROPERTY,
                                value: values[INTERVENTION_CREATED_FROM_AVATAR_PROPERTY],
                                fieldType: 'string',
                                private: false,
                            },
                        ],
                        groupAvatars: groupAvatars && groupAvatars.length > 0 ? [firstGroupAvatarId ?? ''] : [],
                        metadataAvatarType: metadataAvatarType?.id,
                        company: currentUser?.company,
                    },
                    meta: {
                        api: 'v2',
                    },
                },
                {
                    onSuccess: (intervention: Avatar) => {
                        if (values[INTERVENTION_CREATED_FROM_AVATAR_PROPERTY] === INTERVENTION_TYPE_NAME_UNDER_QUOTE) {
                            create(
                                CHANGE_DEMANDS_TO,
                                {
                                    data: {
                                        groupAvatar: values['groupAvatar']?.id,
                                        traceabilityHumanId: '0011',
                                        intervention: intervention?.id,
                                        addInterventionToGroupAvatar: true,
                                    },
                                },
                                {
                                    onSuccess: () => {
                                        notify('ra.notification.created', { type: 'success' });
                                        queryClient.invalidateQueries({ queryKey: [AVATARS, 'getList'] });
                                        queryClient.invalidateQueries({ queryKey: [GROUP_AVATARS, 'getList'] });
                                        handleClose();
                                    },
                                },
                            );
                        } else if (
                            values[INTERVENTION_CREATED_FROM_AVATAR_PROPERTY] ===
                            INTERVENTION_TYPE_NAME_AUDIT_OUT_OF_CONTRACT
                        ) {
                            const yearLastTwoDigits = moment().year().toString().slice(-2);

                            getAlphaId().then((counterData: { counter: number; identifier: string }) => {
                                create(
                                    GROUP_AVATARS,
                                    {
                                        data: {
                                            name: `${yearLastTwoDigits}${addZeros(counterData.counter)}`,
                                            company: currentUser?.company,
                                            type: AVATAR_GROUP_AUDIT_TYPE,
                                            identifiers: [placeAlphaId],
                                        },
                                    },
                                    {
                                        onSuccess: (avatarGroup) => {
                                            create(
                                                AVATARS_ADD_GROUP_AVATARS,
                                                {
                                                    data: {
                                                        groupAvatar: avatarGroup.id,
                                                        avatars: [intervention.id, values.audit],
                                                    },
                                                },
                                                {
                                                    onSuccess: () => {
                                                        create(
                                                            TRACEABILITY_TRACKING_V2_CHANGE_NODE_1,
                                                            {
                                                                data: {
                                                                    avatar: values.audit,
                                                                    idForNodeHumanReadable:
                                                                        AUDIT_STATE_TAKE_INTO_ACCOUNT_HUMAN_ID,
                                                                    version: 1,
                                                                    extraData: [],
                                                                },
                                                            },
                                                            {
                                                                onSuccess: () => {
                                                                    notify('ra.notification.created', {
                                                                        type: 'success',
                                                                    });
                                                                    queryClient.invalidateQueries({
                                                                        queryKey: [AVATARS, 'getList'],
                                                                    });
                                                                    queryClient.invalidateQueries({
                                                                        queryKey: [GROUP_AVATARS],
                                                                    });
                                                                    handleClose();
                                                                },
                                                            },
                                                        );
                                                    },
                                                },
                                            );
                                        },
                                    },
                                );
                            });
                        } else {
                            notify('ra.notification.created', { type: 'success' });
                            queryClient.invalidateQueries({ queryKey: [AVATARS, 'getList'] });
                            handleClose();
                        }
                    },
                    onError: () => notify('messages.intervention.fail_to_create', { type: 'error' }),
                },
            );
        });
    };

    const schema = Joi.object(
        merge(
            {},
            set(
                {},
                'groupAvatar',
                Joi.when(INTERVENTION_CREATED_FROM_AVATAR_PROPERTY, {
                    is: INTERVENTION_TYPE_NAME_VALUES[0],
                    then: Joi.object({ id: Joi.string().required() }).required(),
                }),
            ),
            set(
                {},
                'audit',
                Joi.when(INTERVENTION_CREATED_FROM_AVATAR_PROPERTY, {
                    is: INTERVENTION_TYPE_NAME_VALUES[2],
                    then: Joi.string().required(),
                }),
            ),
            set(
                {},
                INTERVENTION_CREATED_FROM_AVATAR_PROPERTY,
                Joi.string()
                    .valid(...INTERVENTION_TYPE_NAME_VALUES)
                    .required(),
            ),
            // set({}, PLANNED_DATE_AVATAR_PROPERTY, Joi.string().isoDate()),
        ),
    ).messages({
        'any.required': translate('ra.validation.required'),
        'string.base': translate('ra.validation.required'),
    });

    return (
        <DialogTemplate icon={<FiberNew />} title={translate('intervention.create')} open={open} onClose={handleClose}>
            <Form onSubmit={handleSubmit} resolver={joiResolver(schema)}>
                <Box display={'grid'} gap={1}>
                    <FormInputsSeparator>
                        <SelectInput
                            helperText={false}
                            name={INTERVENTION_CREATED_FROM_AVATAR_PROPERTY}
                            source={INTERVENTION_CREATED_FROM_AVATAR_PROPERTY}
                            label={'intervention.form.create_from'}
                            choices={map(INTERVENTION_TYPE_NAME_VALUES, (v) => ({
                                name: `intervention.list.${v}`,
                                id: v,
                            }))}
                        />

                        <FormDataConsumer>
                            {({ formData }) => (
                                <>
                                    {formData[INTERVENTION_CREATED_FROM_AVATAR_PROPERTY] ===
                                        INTERVENTION_TYPE_NAME_UNDER_QUOTE && (
                                        <ReferenceInput
                                            name={'groupAvatar.id'}
                                            source={'groupAvatar.id'}
                                            reference={GROUP_AVATARS}
                                            filter={{
                                                type: AVATAR_GROUP_QUOTE_TYPE,
                                                identifiers: [placeAlphaId],
                                            }}
                                            queryOptions={{ enabled: !!placeAlphaId && !isLoadingPlace }}
                                        >
                                            <AutocompleteInput
                                                label='intervention.form.select_quote'
                                                source='groupAvatar'
                                                size={'small'}
                                                optionText='name'
                                            />
                                        </ReferenceInput>
                                    )}
                                    {formData[INTERVENTION_CREATED_FROM_AVATAR_PROPERTY] ===
                                        INTERVENTION_TYPE_NAME_AUDIT_OUT_OF_CONTRACT && (
                                        <ReferenceInput
                                            name={'audit'}
                                            source={'audit'}
                                            reference={AVATARS}
                                            filter={{
                                                'metadataAvatarType.name': AUDIT_METADATA_AVATAR_TYPE_NAME,
                                                parent: placeId,
                                                serialization_groups: ['read_avatar_current_node', 'read_node'],
                                                enabled: true,
                                                'currentNode.name': 'Audit',
                                            }}
                                            queryOptions={{ enabled: !!placeAlphaId && !isLoadingPlace }}
                                        >
                                            <AutocompleteInput
                                                label='intervention.form.select_audit'
                                                source='audit'
                                                size={'small'}
                                                optionText='alphaId'
                                            />
                                        </ReferenceInput>
                                    )}
                                </>
                            )}
                        </FormDataConsumer>
                        {/*<DateInput*/}
                        {/*    label={'intervention.list.planned_date'}*/}
                        {/*    helperText={false}*/}
                        {/*    name={PLANNED_DATE_AVATAR_PROPERTY}*/}
                        {/*    source={PLANNED_DATE_AVATAR_PROPERTY}*/}
                        {/*/>*/}
                    </FormInputsSeparator>

                    <ButtonsBlock>
                        <Button
                            onClick={handleClose}
                            disabled={isLoadingCreate || isLoadingGetAlphaId || isLoadingPlace}
                        >
                            {translate('ra.action.back')}
                        </Button>

                        <LoadingButton
                            data-cy={'create-intervention-button'}
                            loading={
                                isLoading ||
                                isLoadingCreate ||
                                isLoadingGetAlphaId ||
                                isLoadingGroup ||
                                isLoadingPlace ||
                                !groupAvatars
                            }
                            type={'submit'}
                            variant={'contained'}
                        >
                            {translate('ra.action.create')}
                        </LoadingButton>
                    </ButtonsBlock>
                </Box>
            </Form>
        </DialogTemplate>
    );
};

export default CreateIntervention;
